'use client'

/**
 * Client desk rail — destinations → Pulse → Watch → Trade/Chat/Help on the rail floor.
 * Chat is also a navbar control on the far right. Digit keys 1–7 jump destinations.
 */

import React, { useEffect, useMemo } from 'react'
import { Tooltip } from 'antd'
import {
  MessageOutlined,
  QuestionCircleOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import { usePathname, useRouter } from 'next/navigation'
import { getIcon } from '@/shared/utils/sidebar/adminSidebarManager'
import type { ClientSidebarItem } from '@/shared/utils/sidebar/clientSidebarConfig'
import { DeskChassis } from '@/shared/components/desk'
import { PulseWidgetHolder } from './PulseWidgetHolder'
import { RailWatchStrip } from './RailWatchStrip'
import styles from './Sidebar.module.css'

type RailSection = {
  id: string
  title: string
  keys: readonly string[]
}

const SECTIONS: readonly RailSection[] = [
  { id: 'desk', title: 'Primary', keys: ['client-dashboard', 'markets', 'portfolio'] },
  { id: 'operate', title: 'Action', keys: ['agents', 'trading'] },
  { id: 'record', title: 'Utility', keys: ['settings', 'activity'] },
]

const RAIL_DIGITS: Record<string, string> = {
  '1': 'client-dashboard',
  '2': 'markets',
  '3': 'portfolio',
  '4': 'agents',
  '5': 'trading',
  '6': 'activity',
  '7': 'settings',
}

const RAIL_KEYS: Record<string, string> = Object.fromEntries(
  Object.entries(RAIL_DIGITS).map(([digit, key]) => [key, digit])
)

export function isEditableHotkeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return Boolean(target.closest('input, textarea, select, [contenteditable="true"]'))
}

export function deskRailRouteForDigit(
  key: string,
  routes: ReadonlyMap<string, string>
): string | null {
  const itemKey = RAIL_DIGITS[key]
  if (!itemKey) return null
  return routes.get(itemKey) ?? null
}

export type ClientDeskRailProps = {
  items: ClientSidebarItem[]
  selectedKey: string
  collapsed: boolean
  navId: string
  onNavigate: (route: string) => void
}

export function ClientDeskRail({
  items,
  selectedKey,
  collapsed,
  navId,
  onNavigate,
}: ClientDeskRailProps) {
  const router = useRouter()
  const pathname = usePathname()
  const byKey = useMemo(() => {
    const map = new Map<string, ClientSidebarItem>()
    for (const item of items) map.set(item.key, item)
    return map
  }, [items])

  const tradingIconName = byKey.get('trading')?.icon
  const TradeIcon = ((tradingIconName ? getIcon(tradingIconName) : null) ||
    ShoppingCartOutlined) as React.ComponentType<{ className?: string }>

  const digitRoutes = useMemo(() => {
    const map = new Map<string, string>()
    for (const [key, item] of byKey) map.set(key, item.route)
    return map
  }, [byKey])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey || event.metaKey || event.ctrlKey || event.repeat) return
      if (isEditableHotkeyTarget(event.target)) return
      const route = deskRailRouteForDigit(event.key, digitRoutes)
      if (!route) return
      event.preventDefault()
      onNavigate(route)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [digitRoutes, onNavigate])

  return (
    <DeskChassis>
    <div className={styles.railBody}>
      <div className={styles.railMain}>
      <div id={navId} className={styles.railNav} role="navigation" aria-label="Primary navigation">
        {SECTIONS.map((section) => {
          const sectionItems = section.keys
            .map((key) => byKey.get(key))
            .filter((item): item is ClientSidebarItem => Boolean(item))
          if (sectionItems.length === 0) return null

          return (
            <section key={section.id} className={styles.railSection} aria-label={section.title}>
              {!collapsed && (
                <h2 className={styles.sectionTitle}>{section.title}</h2>
              )}
              <ul className={styles.railList} role="list">
                {sectionItems.map((item) => {
                  const active = item.key === selectedKey
                  const Icon = getIcon(item.icon) as React.ComponentType<{
                    className?: string
                  }>
                  const shortcut = RAIL_KEYS[item.key]
                  const link = (
                    <button
                      type="button"
                      className={`${styles.railLink} ${active ? styles.railLinkActive : ''}`}
                      aria-current={active ? 'page' : undefined}
                      aria-label={item.label}
                      aria-keyshortcuts={shortcut || undefined}
                      title={shortcut ? `${item.label} · ${shortcut}` : item.label}
                      onClick={() => onNavigate(item.route)}
                    >
                      <span className={styles.railIcon} aria-hidden>
                        <Icon />
                      </span>
                      {!collapsed && (
                        <>
                          <span className={styles.railLabel}>{item.label}</span>
                          {shortcut ? (
                            <kbd className={styles.railKbd} aria-hidden>
                              {shortcut}
                            </kbd>
                          ) : null}
                        </>
                      )}
                    </button>
                  )

                  return (
                    <li key={item.key} className={styles.railItem}>
                      {collapsed ? (
                        <Tooltip title={item.label} placement="right">
                          {link}
                        </Tooltip>
                      ) : (
                        link
                      )}
                    </li>
                  )
                })}
              </ul>
            </section>
          )
        })}
      </div>

      {/* No Pulse on the desk: live /trading and /trading/preview (isTradingDeskSidebarPath) */}
      {pathname !== '/trading' && pathname !== '/trading/preview' && !pathname.startsWith('/trading/preview/') ? (
        <PulseWidgetHolder collapsed={collapsed} />
      ) : null}
      <RailWatchStrip collapsed={collapsed} />
      </div>

      <section
        className={styles.shortcutBay}
        aria-label="Shortcuts"
        data-collapsed={collapsed ? 'true' : undefined}
      >
        {!collapsed && <h2 className={styles.sectionTitle}>Shortcuts</h2>}
        <ul className={styles.railList} role="list">
          {(
            [
              {
                label: 'Trade',
                href: '/trading',
                aria: 'Open trade desk',
                Icon: TradeIcon,
              },
              {
                label: 'Chat',
                href: '/chat-ai',
                aria: 'Open desk chat',
                Icon: MessageOutlined,
              },
              {
                label: 'Help',
                href: '/help',
                aria: 'Open help',
                Icon: QuestionCircleOutlined,
              },
            ] as const
          ).map((action) => {
            const current =
              pathname === action.href || pathname.startsWith(`${action.href}/`)
            const btn = (
              <button
                type="button"
                className={`${styles.railLink} ${current ? styles.railLinkActive : ''}`}
                aria-label={action.aria}
                aria-current={current ? 'page' : undefined}
                onClick={() => router.push(action.href)}
              >
                <span className={styles.railIcon} aria-hidden>
                  <action.Icon />
                </span>
                {!collapsed && <span className={styles.railLabel}>{action.label}</span>}
              </button>
            )
            return (
              <li key={action.href} className={styles.railItem}>
                {collapsed ? (
                  <Tooltip title={action.label} placement="right">
                    {btn}
                  </Tooltip>
                ) : (
                  btn
                )}
              </li>
            )
          })}
        </ul>
      </section>
    </div>
    </DeskChassis>
  )
}

export default ClientDeskRail
